'use client';

import { useEffect, useRef } from 'react';
import Image from 'next/image';
import { useRouter } from 'next/navigation';
import { getStoredToken, useAuthStore } from '@/store/authStore';
import { rolesApi, PermissionsMap } from '@/lib/api';
import { getLandingRoute } from '@/lib/getLandingRoute';

export default function HomePage() {
  const router = useRouter();
  const startedRef = useRef(false);

  useEffect(() => {
    if (startedRef.current) return;
    startedRef.current = true;

    const splash = document.getElementById('boot-splash');
    if (splash) splash.style.display = 'none';

    const token = getStoredToken('access_token');
    if (!token) {
      router.push('/login');
      return;
    }

    // Verify auth and resolve landing route based on permissions
    useAuthStore.getState().checkAuth().then(async () => {
      if (!useAuthStore.getState().isAuthenticated) {
        router.push('/login');
        return;
      }
      try {
        const permissions: PermissionsMap = await rolesApi.getMyPermissions();
        router.push(getLandingRoute(permissions, useAuthStore.getState().user));
      } catch {
        router.push('/atlas');
      }
    });
  }, [router]);

  return (
    <div className="min-h-screen flex flex-col items-center justify-center gap-8 bg-gray-50 dark:bg-neutral-950">
      <div className="relative w-24 h-24 flex items-center justify-center">
        <div className="absolute inset-0 rounded-full border-2 border-brand-dark/8 animate-[spin_4s_linear_infinite]">
          <div className="absolute -top-1 left-1/2 -translate-x-1/2 w-2 h-2 rounded-full bg-brand-light shadow-[0_0_8px_rgba(66,165,245,0.6)]" />
        </div>
        <div className="absolute inset-3 rounded-full border border-brand-light/12 animate-[spin_3s_linear_infinite_reverse]">
          <div className="absolute -bottom-0.5 left-1/2 -translate-x-1/2 w-1.5 h-1.5 rounded-full bg-brand-dark shadow-[0_0_6px_rgba(21,101,160,0.5)]" />
        </div>
        <div className="relative z-10 w-12 h-12 rounded-xl overflow-hidden shadow-lg animate-pulse">
          <Image src="/logo.png" alt="Atlas" width={48} height={48} priority className="w-full h-full object-cover" />
        </div>
      </div>
      <div className="w-48 h-1 rounded-full bg-gray-100 dark:bg-neutral-800 overflow-hidden">
        <div className="h-full w-2/5 rounded-full bg-linear-to-r from-brand-dark via-brand-light to-brand-dark animate-[shimmer_1.5s_ease-in-out_infinite]" />
      </div>
    </div>
  );
}
